import { useMemo } from 'react';
import {
  CellRendererSelectorResult,
  ICellRendererParams,
} from 'ag-grid-community';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './checkboxCellRendererDecorator.module.scss';

type CheckboxProps = {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isChecked: boolean;
};

export type CheckboxCellRendererDecoratorProps = {
  cellRendererConfig?: CellRendererSelectorResult;
} & ICellRendererParams &
  CheckboxProps;

export const CheckboxCellRendererDecorator = ({
  cellRendererConfig,
  onChange,
  isChecked,
  ...cellRendererProps
}: CheckboxCellRendererDecoratorProps) => {
  const parent = useMemo(() => {
    if (cellRendererConfig != null) {
      return (
        <cellRendererConfig.component
          {...cellRendererConfig.params}
          {...cellRendererProps}
        />
      );
    }

    return;
  }, [cellRendererConfig, cellRendererProps]);

  const { value, valueFormatted } = cellRendererProps;

  if (value === undefined) {
    return parent;
  }

  const cellValue = valueFormatted ?? value;

  return (
    <div className={styles['root']} data-testid="checkbox-cell">
      {/* Markup copyied over from ag-grid checkbox cell renderer */}
      <div
        {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
        className="ag-selection-checkbox"
        role="presentation"
      >
        <div
          role="presentation"
          data-ref="eCheckbox"
          className="ag-labeled ag-label-align-right ag-checkbox ag-input-field"
        >
          <div
            data-ref="eLabel"
            className="ag-input-field-label ag-label ag-hidden ag-checkbox-label"
            aria-hidden="true"
            role="presentation"
          ></div>
          <div
            data-ref="eWrapper"
            className="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper"
            role="presentation"
          >
            <input
              data-ref="eInput"
              data-testid="checkbox-decorator-cell-input"
              className="ag-input-field-input ag-checkbox-input"
              type="checkbox"
              checked={isChecked}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
      <span className={styles['value']}>{parent ?? cellValue}</span>
    </div>
  );
};
